import {
  db,
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from "../../../config/firebase";
import { ERROR_MESSAGES } from "../types/invitationTypes";
import {
  generateInvitationId,
  generateShortCode,
  validateInvitationData,
  buildInvitationUrl,
  buildShortUrl,
} from "../utils/invitationHelpers";

const BASE_URL = "https://studio.com";

class InvitationService {
  getInvitationCollection() {
    return collection(db, "invitations");
  }

  convertTimestamp(value) {
    if (!value) return null;
    if (typeof value === "object" && value.seconds !== undefined) {
      return new Date(
        value.seconds * 1000 + (value.nanoseconds || 0) / 1000000,
      ).toISOString();
    }
    if (value instanceof Date) return value.toISOString();
    return value;
  }

  convertDocData(docData) {
    if (!docData) return docData;
    const converted = { ...docData };
    ["createdAt", "updatedAt"].forEach((field) => {
      if (converted[field])
        converted[field] = this.convertTimestamp(converted[field]);
    });
    return converted;
  }

  async getInvitations(filters = {}) {
    try {
      let q = query(
        this.getInvitationCollection(),
        orderBy("createdAt", "desc"),
      );
      if (filters.status && filters.status !== "all") {
        q = query(q, where("status", "==", filters.status));
      }
      const snapshot = await getDocs(q);
      let invitations = [];
      snapshot.forEach((docSnap) => {
        invitations.push({
          ...this.convertDocData(docSnap.data()),
          id: docSnap.id,
        });
      });
      invitations = invitations.filter((i) => i.isDeleted !== true);

      if (filters.search) {
        const s = filters.search.toLowerCase();
        invitations = invitations.filter(
          (i) =>
            i.invitationDetails?.title?.toLowerCase().includes(s) ||
            i.invitationDetails?.brideName?.toLowerCase().includes(s) ||
            i.invitationDetails?.groomName?.toLowerCase().includes(s) ||
            i.invitationCode?.toLowerCase().includes(s),
        );
      }
      return invitations;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getInvitationById(id) {
    try {
      const docRef = doc(db, "invitations", id);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) throw new Error(ERROR_MESSAGES.NOT_FOUND);
      return { ...this.convertDocData(docSnap.data()), id: docSnap.id };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createInvitation(data) {
    try {
      const errors = validateInvitationData(data);
      if (Object.keys(errors).length > 0)
        throw new Error(Object.values(errors)[0]);

      const invitationCode = generateInvitationId();
      const shortCode = generateShortCode();

      const invitationData = {
        invitationCode,
        eventId: data.eventId || "",
        status: data.status || "draft",
        invitationDetails: {
          title: data.invitationDetails.title,
          brideName: data.invitationDetails.brideName,
          groomName: data.invitationDetails.groomName,
          invitationMessage: data.invitationDetails.invitationMessage || "",
          eventDate: data.invitationDetails.eventDate,
          startTime: data.invitationDetails.startTime || "",
          venueName: data.invitationDetails.venueName || "",
          venueAddress: data.invitationDetails.venueAddress || "",
          googleMapLink: data.invitationDetails.googleMapLink || "",
        },
        template: {
          templateId: data.template?.templateId || "TMP001",
          templateName: data.template?.templateName || "Royal Gold",
          backgroundImage: data.template?.backgroundImage || null,
          backgroundMobileImage: data.template?.backgroundMobileImage || null,
          primaryColor: data.template?.primaryColor || "#D4AF37",
          secondaryColor: data.template?.secondaryColor || "#FFFFFF",
        },
        images: {
          coverImage: null,
          thumbnail: null,
          groomImage: null,
          brideImage: null,
          ogImage: null,
        },
        share: {
          invitationUrl: buildInvitationUrl(BASE_URL, invitationCode),
          shortUrl: buildShortUrl(BASE_URL, shortCode),
          shortCode,
          qrCodeUrl: null,
          whatsappMessage:
            data.share?.whatsappMessage ||
            "We warmly invite you to our wedding ❤️",
          whatsappImage: null,
        },
        seo: {
          title: data.invitationDetails.title,
          description: data.seo?.description || "Join us on our special day.",
          ogImage: null,
        },
        isDeleted: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const docRef = await addDoc(
        this.getInvitationCollection(),
        invitationData,
      );
      const newDoc = await getDoc(docRef);
      return { ...this.convertDocData(newDoc.data()), id: newDoc.id };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateInvitation(id, data) {
    try {
      const docRef = doc(db, "invitations", id);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) throw new Error(ERROR_MESSAGES.NOT_FOUND);

      const safeData = { ...data };
      delete safeData.id;
      delete safeData.invitationCode;

      await updateDoc(docRef, { ...safeData, updatedAt: serverTimestamp() });
      const updatedDoc = await getDoc(docRef);
      return { ...this.convertDocData(updatedDoc.data()), id: updatedDoc.id };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteInvitation(id) {
    try {
      const docRef = doc(db, "invitations", id);
      await updateDoc(docRef, {
        isDeleted: true,
        updatedAt: serverTimestamp(),
      });
      return id;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async permanentlyDeleteInvitation(id) {
    try {
      await deleteDoc(doc(db, "invitations", id));
      return id;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async publishInvitation(id) {
    return this.updateInvitation(id, { status: "published" });
  }

  async unpublishInvitation(id) {
    return this.updateInvitation(id, { status: "draft" });
  }

  async archiveInvitation(id) {
    return this.updateInvitation(id, { status: "archived" });
  }

  // ============ IMAGES ============

  async updateImage(id, imageKey, url) {
    try {
      const invitation = await this.getInvitationById(id);
      const images = { ...invitation.images, [imageKey]: url };
      const updateData = { images };

      if (imageKey === "coverImage") {
        updateData.seo = { ...invitation.seo, ogImage: url };
        updateData.images = { ...images, ogImage: url };
      }

      return await this.updateInvitation(id, updateData);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateTemplate(id, templateUpdates) {
    try {
      const invitation = await this.getInvitationById(id);
      const template = { ...invitation.template, ...templateUpdates };
      return await this.updateInvitation(id, { template });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateQrCode(id, qrCodeUrl) {
    try {
      const invitation = await this.getInvitationById(id);
      const share = { ...invitation.share, qrCodeUrl };
      return await this.updateInvitation(id, { share });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ============ PUBLIC (GUEST-FACING) ============

  async getPublicInvitation(idOrShortCode) {
    try {
      let invitation = null;
      try {
        invitation = await this.getInvitationById(idOrShortCode);
      } catch {
        const q = query(
          this.getInvitationCollection(),
          where("share.shortCode", "==", idOrShortCode),
        );
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          const docSnap = snapshot.docs[0];
          invitation = {
            ...this.convertDocData(docSnap.data()),
            id: docSnap.id,
          };
        }
      }
      if (!invitation || invitation.status !== "published") {
        throw new Error("Invitation is not available");
      }
      return invitation;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  handleError(error) {
    console.error("Firebase error:", error);
    if (error.code === "permission-denied") {
      return new Error("You don't have permission to perform this action.");
    }
    return new Error(error.message || "An error occurred");
  }
}

const invitationService = new InvitationService();
export default invitationService;
